import { ActionIcon, Box, Button, Card, Collapse, Group, Text, Tooltip } from '@mantine/core'
import { IconChevronDown, IconChevronUp } from '@/components/icons'
import { ProjectIcon } from '@/components/sidebar/ProjectIcon'
import { PreviousTicketsRowsList, type PreviousTicketsRowsListProps } from '@/components/tickets/TicketDetailSidePanels'
import type { TicketListItem } from '@/types/api'

type TicketPreviousTicketsCardProps = {
  customerId?: string | null
  previousTickets: TicketListItem[]
  displayedTickets: TicketListItem[]
  projectCounts: Array<[string, number]>
  previewLimit: number
  projectPick: PreviousTicketsRowsListProps['projectPick']
  onSelectTicket: PreviousTicketsRowsListProps['onSelectTicket']
  onOpenInNewTab: PreviousTicketsRowsListProps['onOpenInNewTab']
  tabStateById?: PreviousTicketsRowsListProps['tabStateById']
  collapsed?: boolean
  onToggleCollapsed?: () => void
  onShowAll: () => void
}

export function TicketPreviousTicketsCard({
  customerId,
  previousTickets,
  displayedTickets,
  projectCounts,
  previewLimit,
  projectPick,
  onSelectTicket,
  onOpenInNewTab,
  tabStateById,
  collapsed = false,
  onToggleCollapsed,
  onShowAll,
}: TicketPreviousTicketsCardProps) {
  if (!customerId || previousTickets.length === 0) return null

  return (
    <Card withBorder padding="sm" radius="md">
      <Group justify="space-between" gap="xs" wrap="nowrap" mb={collapsed ? 0 : 6}>
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="xs">
            Прошлые обращения ({previousTickets.length})
          </Text>
          {collapsed ? (
            <Text size="xs" c="dimmed">
              Свернуто
            </Text>
          ) : null}
        </Box>
        {onToggleCollapsed ? (
          <Tooltip label={collapsed ? 'Развернуть' : 'Свернуть'} withArrow>
            <ActionIcon size="sm" variant="subtle" onClick={onToggleCollapsed} aria-label="Свернуть прошлые обращения">
              {collapsed ? <IconChevronDown size={14} stroke={1.5} /> : <IconChevronUp size={14} stroke={1.5} />}
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Group>
      <Collapse in={!collapsed}>
        {projectCounts.length > 0 ? (
          <Box
            mb={8}
            pb={8}
            style={{
              borderBottom: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
            }}
          >
            <Group gap={12} wrap="wrap">
              {projectCounts.map(([projectId, count]) => {
                const pick =
                  projectId === '__unknown__'
                    ? projectPick('', 'Неизвестный проект')
                    : projectPick(
                        projectId,
                        previousTickets.find((ticket) => ticket.project_id === projectId)?.project_name,
                      )
                return (
                  <Tooltip key={projectId} label={pick.name} withArrow position="top">
                    <Group gap={4} wrap="nowrap" style={{ cursor: 'default' }}>
                      <ProjectIcon project={pick} boxSize={16} />
                      <Text component="span" size="xs" fw={600}>
                        ({count})
                      </Text>
                    </Group>
                  </Tooltip>
                )
              })}
            </Group>
          </Box>
        ) : null}
        <PreviousTicketsRowsList
          items={displayedTickets}
          projectPick={projectPick}
          onSelectTicket={onSelectTicket}
          onOpenInNewTab={onOpenInNewTab}
          tabStateById={tabStateById}
        />
        {previousTickets.length > previewLimit ? (
          <Button size="compact-xs" variant="subtle" fullWidth mt={4} onClick={onShowAll}>
            Показать все ({previousTickets.length})
          </Button>
        ) : null}
      </Collapse>
    </Card>
  )
}
